<template>
  <div :class="ns.e('selection-dropdown')">
    <jd-dropdown
      trigger="click"
      placement="bottom-start"
      :disabled="isDisabled"
      @command="handleDropdownCommand"
      @visible-change="handleDropdownVisibleChange"
    >
      <div :class="ns.e('selection-trigger')" @click.stop>
        <jd-checkbox
          :model-value="isAllSelected"
          :indeterminate="isIndeterminate"
          :disabled="isDisabled"
          :size="tableSize"
          @click.prevent.stop
        />
        <jd-icon :class="{ 'is-expanded': isDropdownVisible }">
          <AngleDown />
        </jd-icon>
      </div>
      <template #dropdown>
        <jd-dropdown-menu>
          <jd-dropdown-item
            command="current-page"
            :style="
              isCurrentPageSelected
                ? {
                    color: 'var(--jd-color-brand-normal)',
                  }
                : {}
            "
          >
            {{ currentPageText }}
          </jd-dropdown-item>
          <jd-dropdown-item
            command="all-data"
            :style="
              isAllDataSelected
                ? {
                    color: 'var(--jd-color-brand-normal)',
                  }
                : {}
            "
          >
            {{ allDataText }}
          </jd-dropdown-item>
        </jd-dropdown-menu>
      </template>
    </jd-dropdown>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted } from 'vue'
import { AngleDown } from '@jd/jdesign-icons-vue'
import JdCheckbox from '@jdesign-vue/components/checkbox'
import {
  JdDropdown,
  JdDropdownItem,
  JdDropdownMenu,
} from '@jdesign-vue/components/dropdown'
import { JdIcon } from '@jdesign-vue/components/icon'
import { useLocale, useNamespace } from '@jdesign-vue/hooks'

import type { ComponentSize } from '@jdesign-vue/constants'

const props = defineProps<{
  modelValue: boolean
  indeterminate: boolean
  disabled: boolean
  size: ComponentSize
  onToggleAllSelection: () => void
  options?: {
    currentPageText?: string
    allDataText?: string
  }
}>()

const emit = defineEmits<{
  (e: 'select-all-data', value: boolean): void
  (e: 'current-page-toggle', value: boolean): void
}>()

const { t } = useLocale()
const ns = useNamespace('table')
const isDropdownVisible = ref(false)
// 使用模块级变量来存储选择模式，确保跨组件实例持久化
let globalSelectedMode: 'current-page' | 'all-data' | null = null
const selectedMode = ref<'current-page' | 'all-data' | null>(null)

const isAllSelected = computed(() => props.modelValue)
const isIndeterminate = computed(() => props.indeterminate)
const isDisabled = computed(() => props.disabled)
const tableSize = computed(() => props.size)

const currentPageText = computed(() => t('el.table.selectCurrentText'))
const allDataText = computed(() => t('el.table.selectAllText'))

// 计算选中状态
const isCurrentPageSelected = computed(() => {
  return selectedMode.value === 'current-page'
})

const isAllDataSelected = computed(() => {
  return selectedMode.value === 'all-data'
})

// 组件挂载时从全局状态恢复
onMounted(() => {
  selectedMode.value = globalSelectedMode
})

const handleDropdownCommand = (command: string) => {
  if (command === 'current-page') {
    // 切换当页全选状态
    if (selectedMode.value === 'current-page') {
      selectedMode.value = null
      globalSelectedMode = null
      // 向外暴露事件，selectedMode.value = null时为false
      emit('current-page-toggle', false)
    } else {
      selectedMode.value = 'current-page'
      globalSelectedMode = 'current-page'
      // 向外暴露事件，selectedMode.value不为null时为true
      emit('current-page-toggle', true)
    }
    // 当前页全选只影响当前页
    // props.onToggleAllSelection()
  } else if (command === 'all-data') {
    // 切换全选模式，与当页模式互斥
    if (selectedMode.value === 'all-data') {
      selectedMode.value = null
      globalSelectedMode = null
      emit('select-all-data', false)
    } else {
      selectedMode.value = 'all-data'
      globalSelectedMode = 'all-data'
      emit('select-all-data', true)
    }
  }
}
// 监听 props 变化，自动调整选择模式
watch(
  [() => props.modelValue, () => props.indeterminate],
  ([modelValue, indeterminate]) => {
    if (!modelValue && !indeterminate) {
      if (globalSelectedMode === 'current-page') {
        selectedMode.value = null
        emit('current-page-toggle', false)
      }
    }
  },
  { immediate: true }
)

const handleDropdownVisibleChange = (visible: boolean) => {
  isDropdownVisible.value = visible
}
</script>
